import { useState, useMemo, useCallback } from 'react';
import { SiteSelector } from './SiteSelector';
import { WellSelector } from './WellSelector';
import { DateRangeFilter } from './DateRangeFilter';
import { NonDetectToggle } from './NonDetectToggle';
import { ThemeToggle } from './ThemeToggle';
import type { FilterSiteItem, FilterWellItem, FilterAnalyteItem, NdHandling } from '@/api/dataVault';
import { useDataVaultStore } from '@/store/dataVault';
import type { AvailableLibrary } from '../hooks/useRegulatoryLines';

type DataSource = 'chemistry' | 'water-levels' | 'compliance' | 'qaqc';

interface Props {
  // Data
  sites: FilterSiteItem[];
  wells: FilterWellItem[];
  analytes: FilterAnalyteItem[];
  sitesLoading?: boolean;

  // Selected state
  selectedSites: string[];
  selectedWells: string[];
  selectedAnalytes: string[];
  dateFrom: string;
  dateTo: string;
  ndHandling: NdHandling;
  exceedsMclOnly: boolean;

  // Data source toggle
  dataSource: DataSource;
  onDataSourceChange: (ds: DataSource) => void;

  // Regulatory libraries present in the current chart data (drives chip set)
  regulatoryLibraries?: AvailableLibrary[];

  // Callbacks
  onSitesChange: (ids: string[]) => void;
  onWellsChange: (ids: string[]) => void;
  onAnalytesChange: (codes: string[]) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onNdHandlingChange: (v: NdHandling) => void;
  onExceedsMclOnlyChange: (v: boolean) => void;
}

const DATA_SOURCES: { key: DataSource; label: string }[] = [
  { key: 'chemistry', label: 'Chemistry' },
  { key: 'water-levels', label: 'Water Levels' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'qaqc', label: 'QA/QC' },
];

// ─── Active Filter Pills ─────────────────────────────────────────────

function FilterPill({ label, color, onRemove }: { label: string; color: string; onRemove: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 transition-colors"
      style={{
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}30`,
      }}
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:opacity-80 transition-opacity ml-0.5"
        style={{ fontSize: '13px', lineHeight: 1 }}
      >
        &times;
      </button>
    </span>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export function FilterRibbon({
  sites, wells, analytes, sitesLoading,
  selectedSites, selectedWells, selectedAnalytes,
  dateFrom, dateTo, ndHandling, exceedsMclOnly,
  dataSource, onDataSourceChange,
  regulatoryLibraries,
  onSitesChange, onWellsChange, onAnalytesChange,
  onDateFromChange, onDateToChange, onNdHandlingChange,
  onExceedsMclOnlyChange,
}: Props) {
  const [expanded, setExpanded] = useState(true);

  // Group analytes for quick select
  const analyteGroups = useMemo(() => {
    const groups: Record<string, FilterAnalyteItem[]> = {};
    for (const a of analytes) {
      const g = a.group || 'other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(a);
    }
    return groups;
  }, [analytes]);

  // Build active filter pills
  const activePills = useMemo(() => {
    const pills: { key: string; label: string; color: string; onRemove: () => void }[] = [];

    // Sites
    for (const siteId of selectedSites) {
      const site = sites.find((s) => s.id === siteId);
      if (site) {
        pills.push({
          key: `site-${siteId}`,
          label: site.name,
          color: '#3b82f6',
          onRemove: () => onSitesChange(selectedSites.filter((id) => id !== siteId)),
        });
      }
    }

    // Wells (show count if > 3)
    if (selectedWells.length > 3) {
      pills.push({
        key: 'wells-group',
        label: `${selectedWells.length} wells`,
        color: '#10b981',
        onRemove: () => onWellsChange([]),
      });
    } else {
      for (const wellId of selectedWells) {
        const well = wells.find((w) => w.id === wellId);
        if (well) {
          pills.push({
            key: `well-${wellId}`,
            label: well.well_name,
            color: '#10b981',
            onRemove: () => onWellsChange(selectedWells.filter((id) => id !== wellId)),
          });
        }
      }
    }

    // Analytes
    for (const code of selectedAnalytes) {
      const analyte = analytes.find((a) => a.code === code);
      if (analyte) {
        pills.push({
          key: `analyte-${code}`,
          label: analyte.name || code,
          color: '#8b5cf6',
          onRemove: () => onAnalytesChange(selectedAnalytes.filter((c) => c !== code)),
        });
      }
    }

    // Date range
    if (dateFrom || dateTo) {
      pills.push({
        key: 'date-range',
        label: `${dateFrom || '...'} to ${dateTo || '...'}`,
        color: '#f59e0b',
        onRemove: () => { onDateFromChange(''); onDateToChange(''); },
      });
    }

    // MCL filter
    if (exceedsMclOnly) {
      pills.push({
        key: 'mcl-only',
        label: 'MCL Only',
        color: '#ef4444',
        onRemove: () => onExceedsMclOnlyChange(false),
      });
    }

    return pills;
  }, [selectedSites, selectedWells, selectedAnalytes, dateFrom, dateTo, exceedsMclOnly, sites, wells, analytes, onSitesChange, onWellsChange, onAnalytesChange, onDateFromChange, onDateToChange, onExceedsMclOnlyChange]);

  const clearAll = useCallback(() => {
    onSitesChange([]);
    onWellsChange([]);
    onAnalytesChange([]);
    onDateFromChange('');
    onDateToChange('');
    onExceedsMclOnlyChange(false);
  }, [onSitesChange, onWellsChange, onAnalytesChange, onDateFromChange, onDateToChange, onExceedsMclOnlyChange]);

  // Summary text when collapsed
  const summaryParts: string[] = [];
  if (selectedSites.length) summaryParts.push(`${selectedSites.length} site${selectedSites.length > 1 ? 's' : ''}`);
  if (selectedWells.length) summaryParts.push(`${selectedWells.length} well${selectedWells.length > 1 ? 's' : ''}`);
  if (selectedAnalytes.length) summaryParts.push(selectedAnalytes.join(', '));
  const summaryText = summaryParts.length ? summaryParts.join(' \u00b7 ') : 'No filters applied';

  return (
    <div
      className="rounded-xl border transition-all duration-200"
      style={{
        background: 'var(--dv-bg-card)',
        borderColor: 'var(--dv-border-subtle)',
      }}
    >
      {/* Top bar: data source tabs + collapse toggle + theme toggle */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: 'var(--dv-border-subtle)' }}
      >
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          {DATA_SOURCES.map((ds) => (
            <button
              key={ds.key}
              onClick={() => onDataSourceChange(ds.key)}
              className="px-3 py-1 text-[11px] font-medium tracking-wider rounded-md transition-all duration-150 whitespace-nowrap"
              style={{
                background: dataSource === ds.key ? 'var(--dv-accent-cyan-muted)' : 'transparent',
                color: dataSource === ds.key ? 'var(--dv-accent-cyan)' : 'var(--dv-text-muted)',
                border: dataSource === ds.key ? '1px solid var(--dv-accent-cyan-border)' : '1px solid transparent',
              }}
            >
              {ds.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Collapse toggle with summary */}
          {!expanded && (
            <span
              className="text-[11px] mr-2 truncate max-w-[180px] sm:max-w-[300px]"
              style={{ color: 'var(--dv-text-muted)' }}
            >
              {summaryText}
            </span>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg transition-colors text-xs"
            style={{
              color: 'var(--dv-text-muted)',
              background: 'var(--dv-pill-bg)',
              border: '1px solid var(--dv-border-subtle)',
            }}
            title={expanded ? 'Collapse filters' : 'Expand filters'}
          >
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <ThemeToggle />
        </div>
      </div>

      {/* Expandable filter controls */}
      <div
        className="transition-all duration-300"
        style={{
          maxHeight: expanded ? '300px' : '0',
          opacity: expanded ? 1 : 0,
          overflow: expanded ? 'visible' : 'hidden',
        }}
      >
        <div className="px-4 py-3 space-y-3">
          {/* Row: selectors */}
          <div className="flex flex-col lg:flex-row flex-wrap items-start lg:items-center gap-2 lg:gap-3">
            <SiteSelector
              sites={sites}
              selected={selectedSites}
              onChange={onSitesChange}
              loading={sitesLoading}
            />
            <WellSelector
              wells={wells}
              selected={selectedWells}
              onChange={onWellsChange}
            />

            {/* Analyte quick filters */}
            <div className="flex items-center gap-1.5">
              <span
                className="text-[10px] tracking-wider"
                style={{ color: 'var(--dv-text-muted)' }}
              >
                ANALYTES
              </span>
              {['btex', 'tph'].map((group) => {
                const groupCodes = (analyteGroups[group] || []).map((a) => a.code);
                const isActive = selectedAnalytes.some((c) => groupCodes.includes(c));
                return (
                  <button
                    key={group}
                    onClick={() => {
                      if (isActive) {
                        onAnalytesChange(selectedAnalytes.filter((c) => !groupCodes.includes(c)));
                      } else {
                        onAnalytesChange([...new Set([...selectedAnalytes, ...groupCodes])]);
                      }
                    }}
                    className="px-2 py-0.5 text-[10px] rounded-full transition-all"
                    style={{
                      background: isActive ? 'var(--dv-accent-cyan-muted)' : 'var(--dv-pill-bg)',
                      color: isActive ? 'var(--dv-accent-cyan)' : 'var(--dv-pill-text)',
                      border: `1px solid ${isActive ? 'var(--dv-accent-cyan-border)' : 'var(--dv-pill-border)'}`,
                    }}
                  >
                    {group.toUpperCase()}
                  </button>
                );
              })}
              {/* Individual analytes dropdown */}
              <select
                multiple={false}
                value=""
                onChange={(e) => {
                  const code = e.target.value;
                  if (code && !selectedAnalytes.includes(code)) {
                    onAnalytesChange([...selectedAnalytes, code]);
                  }
                }}
                className="text-[11px] rounded px-1.5 py-0.5"
                style={{
                  background: 'var(--dv-bg-input)',
                  color: 'var(--dv-text-secondary)',
                  border: '1px solid var(--dv-border-default)',
                }}
              >
                <option value="">+ Add analyte</option>
                {analytes
                  .filter((a) => !selectedAnalytes.includes(a.code))
                  .map((a) => (
                    <option key={a.code} value={a.code}>{a.name || a.code}</option>
                  ))}
              </select>
            </div>

            {/* Date range */}
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={onDateFromChange}
              onDateToChange={onDateToChange}
            />

            {/* ND handling */}
            <NonDetectToggle
              value={ndHandling}
              onChange={onNdHandlingChange}
            />

            {/* MCL only toggle */}
            <button
              onClick={() => onExceedsMclOnlyChange(!exceedsMclOnly)}
              className="px-2.5 py-1 text-[10px] rounded-full transition-all"
              style={{
                background: exceedsMclOnly ? 'rgba(239, 68, 68, 0.15)' : 'var(--dv-pill-bg)',
                color: exceedsMclOnly ? '#ef4444' : 'var(--dv-pill-text)',
                border: `1px solid ${exceedsMclOnly ? 'rgba(239, 68, 68, 0.3)' : 'var(--dv-pill-border)'}`,
              }}
            >
              MCL ONLY
            </button>

            {/* Regulatory standard visibility toggles */}
            <RegulatoryToggleChips libraries={regulatoryLibraries ?? []} />
          </div>
        </div>
      </div>

      {/* Active filter pills row */}
      {activePills.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-t"
          style={{ borderColor: 'var(--dv-border-subtle)' }}
        >
          {activePills.map((pill) => (
            <FilterPill
              key={pill.key}
              label={pill.label}
              color={pill.color}
              onRemove={pill.onRemove}
            />
          ))}
          {activePills.length > 1 && (
            <button
              onClick={clearAll}
              className="text-[10px] px-2 py-0.5 transition-colors"
              style={{ color: 'var(--dv-text-muted)' }}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ── Regulatory Standard Toggle Chips ─────────────────────────────────
//
// Data-driven: renders one chip per library actually present in the current
// chart response. Labels, colors, and keys all come from the backend — the
// frontend treats library identifiers as opaque. If the dataset has no
// regulatory limits configured, the group hides itself entirely.

function RegulatoryToggleChips({ libraries }: { libraries: AvailableLibrary[] }) {
  const hidden = useDataVaultStore((s) => s.hiddenRegulatoryLibraries);
  const toggle = useDataVaultStore((s) => s.toggleRegulatoryLibrary);

  if (libraries.length === 0) return null;

  return (
    <div className="flex items-center gap-1 ml-2 pl-2 border-l border-mono-700/30">
      <span className="text-[9px] text-mono-500 mr-0.5 whitespace-nowrap">STANDARDS:</span>
      {libraries.map(({ key, label, color }) => {
        const isHidden = hidden.has(key);
        return (
          <button
            key={key}
            onClick={() => toggle(key)}
            className="px-1.5 py-0.5 text-[9px] rounded-full border transition-all"
            style={{
              borderColor: isHidden ? 'var(--dv-pill-border)' : `${color}50`,
              color: isHidden ? 'var(--dv-pill-text)' : color,
              background: isHidden ? 'transparent' : `${color}15`,
              opacity: isHidden ? 0.4 : 1,
            }}
            title={isHidden ? `Show ${label} lines` : `Hide ${label} lines`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
